import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import JerseySlot from './components/JerseySlot';
import { API_BASE_URL } from './config';
import fantasyLogo from './assets/jerseys/Logo 36.png';
import loadingLogo from './assets/jerseys/Logo 36.png';

// 5 titulares: 2 interiores arriba, 3 perimetrales abajo - Cancha vertical
const starters = [
  { label: 'INTERIOR', x: 33, y: 18, positionType: 'Interior' },
  { label: 'INTERIOR', x: 67, y: 18, positionType: 'Interior' },
  { label: 'PERIMETRAL', x: 15, y: 56, positionType: 'Perimetral' },
  { label: 'PERIMETRAL', x: 50, y: 70, positionType: 'Perimetral' },
  { label: 'PERIMETRAL', x: 85, y: 56, positionType: 'Perimetral' },
];

// 5 suplentes: 3 perimetrales, 2 interior
const bench = [
  { label: 'PERIMETRAL', positionType: 'Perimetral' },
  { label: 'PERIMETRAL', positionType: 'Perimetral' },
  { label: 'PERIMETRAL', positionType: 'Perimetral' },
  { label: 'INTERIOR', positionType: 'Interior' },
  { label: 'INTERIOR', positionType: 'Interior' },
];

const API_URL = `${API_BASE_URL}/api/players`;

// El rango de precios se calculará dinámicamente basado en los jugadores disponibles
const getDefaultPriceRange = (players: any[]): [number, number] => {
  if (!players || players.length === 0) {
    return [4, 20]; // Rango por defecto si no hay jugadores
  }
  const prices = players.map(p => parseFloat(p.price || 0)).filter(p => p > 0);
  if (prices.length === 0) {
    return [4, 20];
  }
  const minPrice = Math.floor(Math.min(...prices));
  const maxPrice = Math.ceil(Math.max(...prices));
  // Asegurar un mínimo de rango razonable
  return [Math.max(4, minPrice), Math.max(15, maxPrice + 1)];
};

interface TeamCourtPageProps {
  onTeamConfirmed?: () => void;
}

const TeamCourtPage: React.FC<TeamCourtPageProps> = ({ onTeamConfirmed }) => {
  const navigate = useNavigate();
  
  // Estilos para el slider de rango dual
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      input[type="range"] {
        -webkit-appearance: none;
        appearance: none;
        background: transparent;
      }
      input[type="range"]::-webkit-slider-track {
        background: transparent;
        height: 8px;
      }
      input[type="range"]::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: #3b82f6;
        cursor: pointer;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        margin-top: -8px;
      }
      input[type="range"]::-moz-range-track {
        background: transparent;
        height: 8px;
      }
      input[type="range"]::-moz-range-thumb {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: #3b82f6;
        cursor: pointer;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const [selectedSlot, setSelectedSlot] = useState<{
    type: 'starter' | 'bench';
    idx: number;
    positionType: string;
  } | null>(null);
  const [players, setPlayers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assigned, setAssigned] = useState<{ [key: string]: any }>({});
  const [modalError, setModalError] = useState<string | null>(null);
  const [budget, setBudget] = useState<number>(100);
  
  // Estados para el modal de confirmación
  const [playerToRemove, setPlayerToRemove] = useState<any>(null);
  const [slotToRemove, setSlotToRemove] = useState<{type: 'starter' | 'bench', idx: number} | null>(null);
  
  // Estados para el modal de sustitución
  const [showSubstitutionModal, setShowSubstitutionModal] = useState(false);
  const [substitutionMode, setSubstitutionMode] = useState<'remove' | 'substitute' | null>(null);
  
  // Estado para modal de confirmación de cierre de sesión
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Nuevos estados para filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPosition] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string[]>([]);
  const [selectedClubs] = useState<string[]>([]);
  const [, setDefaultPriceRange] = useState<[number, number]>([4, 20]);
  const [priceRange, setPriceRange] = useState<[number, number]>([4, 20]); // [min, max]
  const [priceSort] = useState<'none' | 'price_asc' | 'price_desc' | 'fantasy_asc' | 'fantasy_desc'>('price_desc');
  const [showClubsDropdown, setShowClubsDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true); // Mostrar al inicio
  const [playersBeingAdded, setPlayersBeingAdded] = useState<Set<string>>(new Set()); // Rastrear jugadores siendo agregados
  
  // Cerrar dropdowns al hacer clic fuera
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showClubsDropdown && !target.closest('.clubs-dropdown-container')) {
        setShowClubsDropdown(false);
      }
      if (showCategoryDropdown && !target.closest('.category-dropdown-container')) {
        setShowCategoryDropdown(false);
      }
    };

    if (showClubsDropdown || showCategoryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showClubsDropdown, showCategoryDropdown]);

  
  // Estado para controlar si el equipo ya fue confirmado
  const [teamName, setTeamName] = useState('Mi Equipo');

  // Función para formatear nombres de equipos para mostrar al usuario
  const formatTeamName = (teamName: string) => {
    if (teamName === 'Penarol') return 'Peñarol';
    return teamName;
  };

  const teamId = localStorage.getItem('fantasyTeamId');

  // Función para traducir mensajes de error del backend
  const translateError = useCallback((errorMessage: string): string => {
    const errorTranslations: { [key: string]: string } = {
      'cannot add more than 3 extranejro players': 'No se pueden agregar más de 3 jugadores extranjeros',
      'cannot add more than 3 extranjero players': 'No se pueden agregar más de 3 jugadores extranjeros',
      'cannot add more than 5 fichanacional players': 'No se pueden agregar más de 5 jugadores de ficha nacional',
      'cannot add more than 5 ficha nacional players': 'No se pueden agregar más de 5 jugadores de ficha nacional',
      'cannot add more than 2 sub23 players': 'No se pueden agregar más de 2 jugadores Sub 23',
      'cannot add more than 3 perimetral players': 'No se pueden agregar más de 3 jugadores perimetrales',
      'cannot add more than 2 interior players': 'No se pueden agregar más de 2 jugadores interiores',
      'cannot add more than 3 players from': 'Ya tienes 3 jugadores de este equipo. Máximo 3 por club',
      'insufficient budget': 'Presupuesto insuficiente',
      'player already in team': 'El jugador ya está en el equipo',
      'player not found': 'Jugador no encontrado',
      'team not found': 'Equipo no encontrado',
      'invalid player data': 'Datos de jugador inválidos',
      'transfer limit exceeded': 'Límite de transferencias excedido',
      'transfer window closed': 'Ventana de transferencias cerrada',
      'Error adding player to team': 'Error al agregar jugador al equipo',
      'Error updating source player role': 'Error al actualizar rol del jugador origen',
      'Error updating target player role': 'Error al actualizar rol del jugador destino',
      'Error removing player from team': 'Error al eliminar jugador del equipo',
      'Error confirmando equipo': 'Error al confirmar equipo',
      'Network error': 'Error de conexión',
      'Error loading players': 'Error al cargar jugadores'
    };

    // Normalizar el mensaje para búsqueda
    const normalizedMessage = errorMessage.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
    
    // Buscar traducción exacta
    if (errorTranslations[normalizedMessage]) {
      return errorTranslations[normalizedMessage];
    }

    // Buscar traducción parcial para mensajes más complejos
    for (const [key, translation] of Object.entries(errorTranslations)) {
      if (normalizedMessage.includes(key) || errorMessage.toLowerCase().includes(key)) {
        return translation;
      }
    }

    // Si no hay traducción, devolver el mensaje original
    return errorMessage;
  }, []);

  // Función para mostrar modal de confirmación de cierre de sesión
  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  // Función para cerrar sesión (ejecutada después de confirmar)
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('fantasyTeamId');
    navigate('/auth', { replace: true });
  };


  // IDs de jugadores ya asignados
  const assignedPlayerIds = useMemo(() => {
    const ids = Object.values(assigned).map(p => p?.id).filter(Boolean);
    return ids;
  }, [assigned]);

  // Contar jugadores asignados
  const assignedPlayersCount = useMemo(() => 
    Object.values(assigned).filter((p: any) => p !== null).length,
    [assigned]
  );

  // Calcular costo/beneficio de un swap
  const calculateSwapCost = (newPlayer: any, existingPlayer: any | null) => {
    if (!existingPlayer) {
      return {
        cost: parseFloat(newPlayer.price),
        benefit: 0,
        netCost: parseFloat(newPlayer.price)
      };
    }

    const cost = parseFloat(newPlayer.price);
    const benefit = parseFloat(existingPlayer.price);
    const netCost = parseFloat((cost - benefit).toFixed(2));

    return { cost, benefit, netCost };
  };

  // Obtener presupuesto y nombre del equipo del backend
  const fetchBudget = useCallback(async () => {
    if (!teamId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/fantasy-teams/${teamId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        // Cargar nombre del equipo
        if (data.name) {
          setTeamName(data.name);
        }
        // Cargar presupuesto
        if (typeof data.budgetRemaining === 'number') {
          setBudget(data.budgetRemaining);
        } else {
          setBudget(100);
        }
      } else {
        setBudget(100);
      }
    } catch (error) {
      setBudget(100);
    }
  }, [teamId]);

  // Cargar jugadores existentes del equipo
  const loadExistingPlayers = useCallback(async () => {
    if (!teamId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/fantasy-teams/${teamId}/players`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const playersResponse = await res.json();
        const players = playersResponse.data;

        const newAssigned: { [key: string]: any } = {};
        
        // Separar jugadores por rol
        const titulares = players.filter((p: any) => p.role === 'Titular');
        const suplentes = players.filter((p: any) => p.role === 'Suplente');

        // Mapear titulares por posición exacta primero
        for (let i = 0; i < starters.length && titulares.length > 0; i++) {
          const slot = starters[i];
          const matchingPlayer = titulares.find((p: any) => 
            p.positionType === slot.positionType && 
            !Object.values(newAssigned).some(assigned => assigned?.id === p.id)
          );
          
          if (matchingPlayer) {
            newAssigned[`starter-${i}`] = matchingPlayer;
          }
        }
        
        // Si quedan titulares sin asignar, llenar slots vacíos
        const unassignedTitulares = titulares.filter((p: any) => 
          !Object.values(newAssigned).some(assigned => assigned?.id === p.id)
        );
        
        for (let i = 0; i < starters.length && unassignedTitulares.length > 0; i++) {
          if (!newAssigned[`starter-${i}`]) {
            const player = unassignedTitulares.shift();
            if (player) {
              newAssigned[`starter-${i}`] = player;
            }
          }
        }
        
        // Mapear suplentes por posición exacta primero
        for (let i = 0; i < bench.length && suplentes.length > 0; i++) {
          const slot = bench[i];
          const matchingPlayer = suplentes.find((p: any) => 
            p.positionType === slot.positionType && 
            !Object.values(newAssigned).some(assigned => assigned?.id === p.id)
          );
          
          if (matchingPlayer) {
            newAssigned[`bench-${i}`] = matchingPlayer;
          }
        }
        
        // Si quedan suplentes sin asignar, llenar slots vacíos
        const unassignedSuplentes = suplentes.filter((p: any) => 
          !Object.values(newAssigned).some(assigned => assigned?.id === p.id)
        );
        
        for (let i = 0; i < bench.length && unassignedSuplentes.length > 0; i++) {
          if (!newAssigned[`bench-${i}`]) {
            const player = unassignedSuplentes.shift();
            if (player) {
              newAssigned[`bench-${i}`] = player;
            }
          }
        }
        
        setAssigned(newAssigned);
      }
    } catch (error) {
      // Error loading existing players
    }
  }, [teamId]);

  // Cargar jugadores disponibles
  const fetchPlayers = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(API_URL, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const playersData = data.data || data;
        setPlayers(playersData);
        // Calcular y actualizar el rango de precios dinámicamente
        const dynamicRange = getDefaultPriceRange(playersData);
        setDefaultPriceRange(dynamicRange);
        // Actualizar el rango actual solo si es la primera carga (valores por defecto)
        setPriceRange(prev => {
          // Si el rango actual es el inicial [4, 20], actualizarlo al dinámico
          if (prev[0] === 4 && prev[1] === 20) {
            return dynamicRange;
          }
          // Si el rango actual está fuera del nuevo rango dinámico, ajustarlo
          if (prev[0] < dynamicRange[0] || prev[1] > dynamicRange[1]) {
            return [
              Math.max(dynamicRange[0], prev[0]),
              Math.min(dynamicRange[1], prev[1])
            ];
          }
          return prev;
        });
      } else {
        setError(translateError('Error loading players'));
      }
    } catch (err) {
      setError(translateError('Network error'));
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [translateError]);



    // Validar composición del equipo
  const validateTeamComposition = (newPlayer: any, slotType: 'starter' | 'bench', slotIdx: number) => {
    // Crear una copia temporal del estado asignado para simular la nueva asignación
    const tempAssigned = { ...assigned };
    tempAssigned[`${slotType}-${slotIdx}`] = newPlayer;
    
    // Contar jugadores por tipo y posición
    const titulares = Object.entries(tempAssigned)
      .filter(([key]) => key.startsWith('starter-'))
      .map(([, player]) => player)
      .filter(Boolean);
    
    const suplentes = Object.entries(tempAssigned)
      .filter(([key]) => key.startsWith('bench-'))
      .map(([, player]) => player)
      .filter(Boolean);
    
    // Validar titulares
    const titularesPerimetral = titulares.filter(p => p.positionType === 'Perimetral');
    const titularesInterior = titulares.filter(p => p.positionType === 'Interior');
    
    if (slotType === 'starter') {
      // Validar que no exceda el límite de posición
      if (newPlayer.positionType === 'Perimetral' && titularesPerimetral.length > 3) {
        return { valid: false, message: 'Ya tienes 3 jugadores perimetrales titulares. Máximo 3 perimetrales.' };
      }
      if (newPlayer.positionType === 'Interior' && titularesInterior.length > 2) {
        return { valid: false, message: 'Ya tienes 2 jugadores interiores titulares. Máximo 2 interiores.' };
      }
    }
    
    // Validar suplentes
    const suplentesPerimetral = suplentes.filter(p => p.positionType === 'Perimetral');
    const suplentesInterior = suplentes.filter(p => p.positionType === 'Interior');
    
    if (slotType === 'bench') {
      // Validar que no exceda el límite de posición
      if (newPlayer.positionType === 'Perimetral' && suplentesPerimetral.length > 3) {
        return { valid: false, message: 'Ya tienes 3 jugadores perimetrales suplentes. Máximo 3 perimetrales.' };
      }
      if (newPlayer.positionType === 'Interior' && suplentesInterior.length > 2) {
        return { valid: false, message: 'Ya tienes 2 jugadores interiores suplentes. Máximo 2 interiores.' };
      }
    }
    
    // Validar máximo 3 jugadores del mismo club
    // Contar jugadores actuales sin incluir el nuevo
    const currentPlayers = Object.entries(assigned)
      .map(([, player]) => player)
      .filter(Boolean);
    
    const teamCounts: { [key: string]: number } = {};
    currentPlayers.forEach(player => {
      if (player.team) {
        teamCounts[player.team] = (teamCounts[player.team] || 0) + 1;
      }
    });
    
    // Si ya hay 3 del mismo equipo, bloquear el 4to
    if (newPlayer.team && teamCounts[newPlayer.team] && teamCounts[newPlayer.team] >= 3) {
      return { valid: false, message: `Ya tienes 3 jugadores de ${newPlayer.team}. Máximo 3 por club.` };
    }
    
    return { valid: true };
  };

    // Función para encontrar el mejor slot disponible para un jugador
  const findBestAvailableSlot = (player: any) => {
    const positionType = player.positionType;
    
    // Primero buscar en titulares
    for (let i = 0; i < starters.length; i++) {
      const slot = starters[i];
      if (slot.positionType === positionType) {
        const key = `starter-${i}`;
        const assignedPlayer = assigned[key];
        if (!assignedPlayer) {
          return { type: 'starter' as const, idx: i, positionType };
        }
      }
    }
    
    // Si no hay titulares libres, buscar en suplentes
    for (let i = 0; i < bench.length; i++) {
      const slot = bench[i];
      if (slot.positionType === positionType) {
        const key = `bench-${i}`;
        const assignedPlayer = assigned[key];
        if (!assignedPlayer) {
          return { type: 'bench' as const, idx: i, positionType };
        }
      }
    }
    
    return null; // No hay slots disponibles
  };

  // Asignar jugador a slot automáticamente
  const handleAssign = async (player: any) => {
    if (!teamId) {
      return;
    }

    // Verificar si el jugador ya está siendo agregado
    if (playersBeingAdded.has(player.id)) {
      return; // Ya está siendo procesado, ignorar el clic
    }

    // Verificar si el jugador ya está en el equipo
    if (assignedPlayerIds.includes(player.id)) {
      // Si ya está en el equipo, eliminarlo
      await handleRemovePlayerById(player);
      return;
    }

    const bestSlot = findBestAvailableSlot(player);
    if (!bestSlot) {
      setModalError(`Ya tienes el máximo de jugadores para esa posición (${player.positionType})`);
      return;
    }
    
    // Validar composición del equipo
    const validation = validateTeamComposition(player, bestSlot.type, bestSlot.idx);
    if (!validation.valid) {
      setModalError(validation.message || 'Error de validación');
      return;
    }
    
    // Marcar jugador como siendo agregado
    setPlayersBeingAdded(prev => new Set(prev).add(player.id));
    
    try {
      const token = localStorage.getItem('token');
      
      // Agregar el jugador al equipo
      const addPlayerRes = await fetch(`${API_BASE_URL}/api/fantasy-teams/${teamId}/players/${player.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          role: bestSlot.type === 'starter' ? 'Titular' : 'Suplente'
        }),
      });
      
      if (!addPlayerRes.ok) {
        const errorData = await addPlayerRes.json();
        // Si el error es que el jugador ya está en el equipo, eliminarlo en lugar de mostrar error
        const errorMessage = errorData.error || errorData.message || '';
        if (errorMessage.toLowerCase().includes('already in team') || errorMessage.toLowerCase().includes('ya está en el equipo')) {
          // El jugador ya está en el equipo, eliminarlo
          await handleRemovePlayerById(player);
          return;
        }
        setModalError(translateError(errorMessage || 'Error adding player to team'));
        return;
      }
      
      // Actualizar estado local inmediatamente
      setAssigned((prev: any) => ({
        ...prev,
        [`${bestSlot.type}-${bestSlot.idx}`]: player
      }));
      
      // Limpiar selección de slot
      setSelectedSlot(null);
      
      // Recargar presupuesto
      await fetchBudget();
      
      // Recargar jugadores existentes
      await loadExistingPlayers();
      
      setModalError(null);
    } catch (error) {
      setModalError(translateError('Network error'));
    } finally {
      // Remover jugador del set de jugadores siendo agregados
      setPlayersBeingAdded(prev => {
        const newSet = new Set(prev);
        newSet.delete(player.id);
        return newSet;
      });
    }
  };

  // Función para limpiar el equipo actual
  const clearTeam = useCallback(async () => {
    if (!teamId) return;
    
    try {
      const token = localStorage.getItem('token');
      const currentPlayers = Object.values(assigned).filter(Boolean) as any[];
      
      // Eliminar todos los jugadores del equipo
      for (const player of currentPlayers) {
        try {
          await fetch(`${API_BASE_URL}/api/fantasy-teams/${teamId}/players/${player.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
        } catch (error) {
          // Error eliminando jugador
        }
      }
      
      // Limpiar estado local
      setAssigned({});
    } catch (error) {
      throw error;
    }
  }, [teamId, assigned]);

  // Función para generar equipo aleatorio
  const generateRandomTeam = useCallback(async () => {
    if (!teamId) {
      setModalError('Error: No se encontró el ID del equipo');
      return;
    }

    if (!players || players.length === 0) {
      setModalError('No hay jugadores disponibles');
      return;
    }

    setModalError(null);
    // NO usar setLoading para evitar la pantalla de carga

    try {
      // Limpiar equipo actual (sin mostrar loading)
      await clearTeam();

      // Requisitos del equipo
      const requirements = {
        titulares: {
          Perimetral: 3,
          Interior: 2
        },
        suplentes: {
          Perimetral: 3,
          Interior: 2
        },
        categorias: {
          Extranjero: 3,
          FichaNacional: 5,
          Sub23: 2
        }
      };

      // Filtrar jugadores disponibles (solo activos y con precio válido)
      const availablePlayers = players.filter((p: any) => 
        p.isActive === true && 
        parseFloat(p.price || 0) > 0 &&
        parseFloat(p.price || 0) <= 100
      );

      if (availablePlayers.length < 10) {
        setModalError('No hay suficientes jugadores disponibles');
        return;
      }

      // Función auxiliar para mezclar array
      const shuffle = (array: any[]) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
      };

      // Intentar generar equipo válido (máximo 100 intentos)
      let selectedTeam: any[] = [];
      let teamCost = 0;
      let attempts = 0;
      const maxAttempts = 100;

      while (attempts < maxAttempts) {
        attempts++;
        selectedTeam = [];
        teamCost = 0;
        
        const teamCounts: { [key: string]: number } = {};
        const categoryCounts = { Extranjero: 0, FichaNacional: 0, Sub23: 0 };
        const titularPositionCounts = { Perimetral: 0, Interior: 0 };
        const suplentePositionCounts = { Perimetral: 0, Interior: 0 };
        const usedPlayerIds = new Set<string>();

        // Mezclar jugadores para aleatoriedad
        const shuffledPlayers = shuffle(availablePlayers);

        // Seleccionar titulares
        for (let i = 0; i < starters.length; i++) {
          const slot = starters[i];
          const costSoFar = teamCost;
          const candidates = shuffledPlayers.filter((p: any) => {
            const cat = p.category as 'Extranjero' | 'FichaNacional' | 'Sub23';
            const pos = p.positionType as 'Perimetral' | 'Interior';
            return p.positionType === slot.positionType &&
              !usedPlayerIds.has(p.id) &&
              (teamCounts[p.team] || 0) < 3 &&
              (categoryCounts[cat] || 0) < (requirements.categorias[cat] || 0) &&
              (titularPositionCounts[pos] || 0) < (requirements.titulares[pos] || 0) &&
              costSoFar + parseFloat(p.price) <= 100;
          });

          if (candidates.length === 0) break;

          const selected = candidates[Math.floor(Math.random() * candidates.length)];
          selectedTeam.push({ ...selected, role: 'Titular', slotType: 'starter', slotIdx: i });
          usedPlayerIds.add(selected.id);
          teamCounts[selected.team] = (teamCounts[selected.team] || 0) + 1;
          
          const selectedCategory = selected.category as 'Extranjero' | 'FichaNacional' | 'Sub23';
          const selectedPositionType = selected.positionType as 'Perimetral' | 'Interior';
          
          categoryCounts[selectedCategory] = (categoryCounts[selectedCategory] || 0) + 1;
          titularPositionCounts[selectedPositionType] = (titularPositionCounts[selectedPositionType] || 0) + 1;
          teamCost += parseFloat(selected.price);
        }

        // Si no se completaron los titulares, reintentar
        if (selectedTeam.length !== 5) continue;

        // Seleccionar suplentes
        for (let i = 0; i < bench.length; i++) {
          const slot = bench[i];
          const costSoFar = teamCost;
          const candidates = shuffledPlayers.filter((p: any) => {
            const cat = p.category as 'Extranjero' | 'FichaNacional' | 'Sub23';
            const pos = p.positionType as 'Perimetral' | 'Interior';
            return p.positionType === slot.positionType &&
              !usedPlayerIds.has(p.id) &&
              (teamCounts[p.team] || 0) < 3 &&
              (categoryCounts[cat] || 0) < (requirements.categorias[cat] || 0) &&
              (suplentePositionCounts[pos] || 0) < (requirements.suplentes[pos] || 0) &&
              costSoFar + parseFloat(p.price) <= 100;
          });

          if (candidates.length === 0) break;

          const selected = candidates[Math.floor(Math.random() * candidates.length)];
          selectedTeam.push({ ...selected, role: 'Suplente', slotType: 'bench', slotIdx: i });
          usedPlayerIds.add(selected.id);
          teamCounts[selected.team] = (teamCounts[selected.team] || 0) + 1;
          
          const selectedCategory = selected.category as 'Extranjero' | 'FichaNacional' | 'Sub23';
          const selectedPositionType = selected.positionType as 'Perimetral' | 'Interior';
          
          categoryCounts[selectedCategory] = (categoryCounts[selectedCategory] || 0) + 1;
          suplentePositionCounts[selectedPositionType] = (suplentePositionCounts[selectedPositionType] || 0) + 1;
          teamCost += parseFloat(selected.price);
        }

        // Si se completó el equipo (5 titulares + 5 suplentes = 10), verificar que gaste al menos 99
        if (selectedTeam.length >= 10) {
          // Si el costo es menor a 99, intentar optimizar seleccionando jugadores más caros
          if (teamCost < 99) {
            // Intentar reemplazar algunos jugadores por versiones más caras
            const remainingBudget = 100 - teamCost;
            const minAdditionalCost = 99 - teamCost;
            
            // Si la diferencia es muy grande, reintentar
            if (remainingBudget < minAdditionalCost) {
              continue;
            }
            
            // Intentar optimizar: buscar jugadores más caros que cumplan las mismas restricciones
            let optimized = false;
            const currentUsedIds = new Set(usedPlayerIds);
            // Recalcular teamCounts sin el jugador actual que estamos considerando reemplazar
            const currentTeamCounts: { [key: string]: number } = {};
            selectedTeam.forEach((p: any) => {
              if (p.team) {
                currentTeamCounts[p.team] = (currentTeamCounts[p.team] || 0) + 1;
              }
            });
            
            for (let i = selectedTeam.length - 1; i >= 0 && teamCost < 99; i--) {
              const currentPlayer = selectedTeam[i];
              const currentPrice = parseFloat(currentPlayer.price);
              const costSoFar = teamCost;
              // Remover temporalmente el jugador actual del conteo de equipos
              if (currentPlayer.team) {
                currentTeamCounts[currentPlayer.team] = (currentTeamCounts[currentPlayer.team] || 0) - 1;
              }
              currentUsedIds.delete(currentPlayer.id);
              
              // Buscar un jugador más caro que cumpla las mismas restricciones
              const betterCandidates = shuffledPlayers.filter((p: any) => {
                return p.positionType === currentPlayer.positionType &&
                  p.category === currentPlayer.category &&
                  !currentUsedIds.has(p.id) &&
                  (currentTeamCounts[p.team] || 0) < 3 &&
                  parseFloat(p.price) > currentPrice &&
                  costSoFar - currentPrice + parseFloat(p.price) <= 100;
              });
              
              if (betterCandidates.length > 0) {
                betterCandidates.sort((a: any, b: any) => parseFloat(b.price) - parseFloat(a.price));
                const betterPlayer = betterCandidates.find((p: any) => 
                  costSoFar - currentPrice + parseFloat(p.price) <= 100
                );
                
                if (betterPlayer) {
                  teamCost = teamCost - currentPrice + parseFloat(betterPlayer.price);
                  selectedTeam[i] = { ...betterPlayer, role: currentPlayer.role, slotType: currentPlayer.slotType, slotIdx: currentPlayer.slotIdx };
                  currentUsedIds.add(betterPlayer.id);
                  // Actualizar conteo de equipos
                  if (betterPlayer.team) {
                    currentTeamCounts[betterPlayer.team] = (currentTeamCounts[betterPlayer.team] || 0) + 1;
                  }
                  optimized = true;
                  
                  // Si ya llegamos a 99, salir del bucle de optimización
                  if (teamCost >= 99) break;
                } else {
                  // Si no encontramos mejor jugador, volver a agregar el actual
                  currentUsedIds.add(currentPlayer.id);
                  if (currentPlayer.team) {
                    currentTeamCounts[currentPlayer.team] = (currentTeamCounts[currentPlayer.team] || 0) + 1;
                  }
                }
              } else {
                // Si no hay candidatos mejores, volver a agregar el actual
                currentUsedIds.add(currentPlayer.id);
                if (currentPlayer.team) {
                  currentTeamCounts[currentPlayer.team] = (currentTeamCounts[currentPlayer.team] || 0) + 1;
                }
              }
            }
            
            // Actualizar usedPlayerIds con los cambios
            usedPlayerIds.clear();
            selectedTeam.forEach((p: any) => usedPlayerIds.add(p.id));
            
            // Si después de optimizar aún no llega a 99, reintentar
            if (teamCost < 99 && !optimized) {
              continue;
            }
          }
          
          // Si llegamos aquí y tenemos 10 jugadores con costo >= 99, salir
          const finalTeamSize = selectedTeam.length;
          if (finalTeamSize >= 10 && teamCost >= 99) {
            break;
          }
        }
      }

      // Si no se pudo generar un equipo válido
      if (selectedTeam.length !== 10 || teamCost < 99) {
        setModalError('No se pudo generar un equipo aleatorio válido que gaste al menos 99 millones. Intenta nuevamente.');
        return;
      }

      // Preparar todas las asignaciones primero
      const newAssigned: { [key: string]: any } = {};
      selectedTeam.forEach((playerData) => {
        const key = `${playerData.slotType}-${playerData.slotIdx}`;
        newAssigned[key] = playerData;
      });

      // Actualizar estado local INMEDIATAMENTE para mostrar los jugadores sin esperar
      setAssigned(newAssigned);
      
      // Actualizar presupuesto localmente inmediatamente
      const totalCost = selectedTeam.reduce((sum, p) => sum + parseFloat(p.price || 0), 0);
      setBudget(100 - totalCost);

      // Agregar jugadores al backend en paralelo (sin bloquear la UI)
      const token = localStorage.getItem('token');
      Promise.all(
        selectedTeam.map(async (playerData) => {
          try {
            const addPlayerRes = await fetch(`${API_BASE_URL}/api/fantasy-teams/${teamId}/players/${playerData.id}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                role: playerData.role
              }),
            });

            if (!addPlayerRes.ok) {
              // Error agregando jugador
            }
          } catch (error) {
            // Error agregando jugador
          }
        })
      ).then(() => {
        // Recargar datos en segundo plano después de que todo se haya agregado
        fetchBudget();
        loadExistingPlayers();
      });

      setModalError(null);
    } catch (error) {
      setModalError('Error al generar equipo aleatorio. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  }, [teamId, players, clearTeam, fetchBudget, loadExistingPlayers]);

  // Confirmar equipo y pasar a la pantalla de competición
  const handleConfirmTeam = async () => {
    if (!teamId) {
      setModalError('Error: No se encontró el ID del equipo');
      return;
    }
    
    if (assignedPlayersCount !== 10) {
      setModalError('Debes tener exactamente 10 jugadores para confirmar tu equipo');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setModalError('Error: No se encontró el token de autenticación');
        return;
      }
      
      const url = `${API_BASE_URL}/api/fantasy-teams/${teamId}/confirm`;
      
      // Llamar al backend para confirmar el equipo
      const confirmRes = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!confirmRes.ok) {
        const errorText = await confirmRes.text();
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          setModalError(`Error del servidor: ${confirmRes.status} ${confirmRes.statusText}`);
          return;
        }
        
        setModalError(translateError(errorData.error || errorData.message || 'Error confirmando equipo'));
        return;
      }
      
      // Redirigir a la pantalla de competición
      if (onTeamConfirmed) {
        onTeamConfirmed();
      } else {
        navigate('/');
      }
    } catch (error) {
      setModalError('Error de red al confirmar equipo');
    }
  };

  // Validar si un cambio violaría las restricciones del equipo
  const validateChange = (playerOut: any, playerIn: any) => {
    // Simular el cambio
    const tempAssigned = { ...assigned };
    
    // Encontrar y reemplazar el jugador que sale
    Object.keys(tempAssigned).forEach(key => {
      if (tempAssigned[key]?.id === playerOut.id) {
        tempAssigned[key] = playerIn;
      }
    });
    
    // Contar jugadores por categoría y posición
    const allPlayers = Object.values(tempAssigned).filter(Boolean) as any[];
    
    const categoryCounts = {
      Extranjero: allPlayers.filter(p => p.category === 'Extranjero').length,
      FichaNacional: allPlayers.filter(p => p.category === 'FichaNacional').length,
      Sub23: allPlayers.filter(p => p.category === 'Sub23').length
    };
    
    const positionCounts = {
      Perimetral: allPlayers.filter(p => p.positionType === 'Perimetral').length,
      Interior: allPlayers.filter(p => p.positionType === 'Interior').length
    };
    
    // Validar restricciones
    if (categoryCounts.Extranjero > 3) return { valid: false, reason: 'extranjeros' };
    if (categoryCounts.FichaNacional > 5) return { valid: false, reason: 'fichas nacionales' };
    if (categoryCounts.Sub23 > 2) return { valid: false, reason: 'sub23' };
    if (positionCounts.Perimetral > 6) return { valid: false, reason: 'perimetrales' };
    if (positionCounts.Interior > 4) return { valid: false, reason: 'interiores' };
    
    // Validar máximo 3 jugadores por club
    const teamCounts: { [key: string]: number } = {};
    allPlayers.forEach(player => {
      if (player.team) {
        teamCounts[player.team] = (teamCounts[player.team] || 0) + 1;
      }
    });
    
    for (const team in teamCounts) {
      if (teamCounts[team] > 3) {
        return { valid: false, reason: 'club' };
      }
    }
    
    return { valid: true };
  };

  // Buscar intercambios compensatorios (reservado para uso futuro)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const findCompensatorySwaps = (playerOut: any, playerIn: any) => {
    const swaps: Array<{ playerOut: any, playerIn: any, slotOut: string, slotIn: string }> = [];
    
    // Si el cambio directo es válido, no necesitamos intercambio compensatorio
    if (validateChange(playerOut, playerIn).valid) {
      return swaps;
    }
    
    // Determinar qué restricción se violaría
    const validation = validateChange(playerOut, playerIn);
    if (!validation.valid) {
      // Buscar jugadores que puedan compensar el cambio
      const allAssignedPlayers = Object.entries(assigned)
        .filter(([, player]) => player && player.id !== playerOut.id)
        .map(([key, player]) => ({ key, player: player as any }));
      
      // Buscar jugadores disponibles en el mercado que puedan compensar
      const availablePlayers = players.filter((p: any) => 
        p.isActive === true && 
        !assignedPlayerIds.includes(p.id) &&
        p.id !== playerIn.id
      );
      
      // Si el problema es de posición (ej: sale interior, entra perimetral)
      if (playerOut.positionType !== playerIn.positionType) {
        // Buscar un perimetral que pueda salir y un interior que pueda entrar (o viceversa)
        const oppositePosition = playerOut.positionType === 'Perimetral' ? 'Interior' : 'Perimetral';
        
        // Buscar jugadores del equipo con la posición opuesta
        const playersToSwapOut = allAssignedPlayers.filter(({ player }) => 
          player.positionType === oppositePosition
        );
        
        // Buscar jugadores disponibles con la posición opuesta
        const playersToSwapIn = availablePlayers.filter((p: any) => 
          p.positionType === oppositePosition &&
          p.category === playerOut.category // Misma categoría para mantener balance
        );
        
        // Buscar combinaciones válidas
        for (const { key: slotOut, player: swapOutPlayer } of playersToSwapOut) {
          for (const swapInPlayer of playersToSwapIn) {
            // Simular ambos cambios simultáneamente
            const tempAssigned = { ...assigned };
            Object.keys(tempAssigned).forEach(k => {
              if (tempAssigned[k]?.id === playerOut.id) {
                tempAssigned[k] = playerIn;
              }
              if (tempAssigned[k]?.id === swapOutPlayer.id) {
                tempAssigned[k] = swapInPlayer;
              }
            });
            
            // Validar el estado final
            const allPlayers = Object.values(tempAssigned).filter(Boolean) as any[];
            const categoryCounts = {
              Extranjero: allPlayers.filter(p => p.category === 'Extranjero').length,
              FichaNacional: allPlayers.filter(p => p.category === 'FichaNacional').length,
              Sub23: allPlayers.filter(p => p.category === 'Sub23').length
            };
            const positionCounts = {
              Perimetral: allPlayers.filter(p => p.positionType === 'Perimetral').length,
              Interior: allPlayers.filter(p => p.positionType === 'Interior').length
            };
            
            const isValid = 
              categoryCounts.Extranjero <= 3 &&
              categoryCounts.FichaNacional <= 5 &&
              categoryCounts.Sub23 <= 2 &&
              positionCounts.Perimetral <= 6 &&
              positionCounts.Interior <= 4;
            
            if (isValid) {
              // Encontrar el slot del jugador que sale
              const [slotType, slotIdx] = slotOut.split('-');
              swaps.push({
                playerOut: swapOutPlayer,
                playerIn: swapInPlayer,
                slotOut: slotOut,
                slotIn: `${slotType}-${slotIdx}` // Mismo slot
              });
            }
          }
        }
      }
      
      // Si el problema es de categoría, buscar compensación similar
      if (playerOut.category !== playerIn.category) {
        // Similar lógica para categorías
        const oppositeCategory = playerOut.category === 'Extranjero' ? 'FichaNacional' : 
                                 playerOut.category === 'FichaNacional' ? 'Extranjero' : 'Sub23';
        
        const playersToSwapOut = allAssignedPlayers.filter(({ player }) => 
          player.category === oppositeCategory
        );
        
        const playersToSwapIn = availablePlayers.filter((p: any) => 
          p.category === oppositeCategory &&
          p.positionType === playerOut.positionType // Misma posición
        );
        
        for (const { key: slotOut, player: swapOutPlayer } of playersToSwapOut) {
          for (const swapInPlayer of playersToSwapIn) {
            const tempAssigned = { ...assigned };
            Object.keys(tempAssigned).forEach(k => {
              if (tempAssigned[k]?.id === playerOut.id) {
                tempAssigned[k] = playerIn;
              }
              if (tempAssigned[k]?.id === swapOutPlayer.id) {
                tempAssigned[k] = swapInPlayer;
              }
            });
            
            const allPlayers = Object.values(tempAssigned).filter(Boolean) as any[];
            const categoryCounts = {
              Extranjero: allPlayers.filter(p => p.category === 'Extranjero').length,
              FichaNacional: allPlayers.filter(p => p.category === 'FichaNacional').length,
              Sub23: allPlayers.filter(p => p.category === 'Sub23').length
            };
            const positionCounts = {
              Perimetral: allPlayers.filter(p => p.positionType === 'Perimetral').length,
              Interior: allPlayers.filter(p => p.positionType === 'Interior').length
            };
            
            const isValid = 
              categoryCounts.Extranjero <= 3 &&
              categoryCounts.FichaNacional <= 5 &&
              categoryCounts.Sub23 <= 2 &&
              positionCounts.Perimetral <= 6 &&
              positionCounts.Interior <= 4;
            
            if (isValid) {
              swaps.push({
                playerOut: swapOutPlayer,
                playerIn: swapInPlayer,
                slotOut: slotOut,
                slotIn: slotOut
              });
            }
          }
        }
      }
    }
    
    return swaps;
  };

  // Obtener suplentes de la misma posición para sustitución
  const getSubstitutesOfSamePosition = (positionType: string, currentSlotType: 'starter' | 'bench', currentSlotIdx: number) => {
    const substitutes: any[] = [];
    
    // Buscar en el otro tipo de slot (si es titular, buscar en suplentes y viceversa)
    const targetSlotType = currentSlotType === 'starter' ? 'bench' : 'starter';
    const targetSlots = targetSlotType === 'starter' ? starters : bench;
    
    for (let i = 0; i < targetSlots.length; i++) {
      const key = `${targetSlotType}-${i}`;
      const player = assigned[key];
      
      if (player && player.positionType === positionType) {
        substitutes.push({
          ...player,
          slotType: targetSlotType,
          slotIdx: i
        });
      }
    }
    
    return substitutes;
  };

  // Realizar sustitución entre jugadores
  const handleSubstitution = async (playerToSubstitute: any, targetSlotType: 'starter' | 'bench', targetSlotIdx: number) => {
    if (!slotToRemove || !teamId) {
      return;
    }
    
    const sourceSlotType = slotToRemove.type;
    const sourceSlotIdx = slotToRemove.idx;
    const sourcePlayer = playerToRemove;
    
    try {
      const token = localStorage.getItem('token');
      
      // Actualizar roles de ambos jugadores
      const updateSourceRes = await fetch(`${API_BASE_URL}/api/fantasy-teams/${teamId}/players/${sourcePlayer.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          role: targetSlotType === 'starter' ? 'Titular' : 'Suplente'
        }),
      });
      
      const updateTargetRes = await fetch(`${API_BASE_URL}/api/fantasy-teams/${teamId}/players/${playerToSubstitute.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          role: sourceSlotType === 'starter' ? 'Titular' : 'Suplente'
        }),
      });
      
      if (!updateSourceRes.ok) {
        const errorData = await updateSourceRes.json();
        setModalError(translateError(errorData.error || errorData.message || 'Error updating source player role'));
        return;
      }
      
      if (!updateTargetRes.ok) {
        const errorData = await updateTargetRes.json();
        setModalError(translateError(errorData.error || errorData.message || 'Error updating target player role'));
        return;
      }
      
      // Actualizar estado local
      setAssigned((prev: any) => {
        const newAssigned = { ...prev };
        newAssigned[`${sourceSlotType}-${sourceSlotIdx}`] = playerToSubstitute;
        newAssigned[`${targetSlotType}-${targetSlotIdx}`] = sourcePlayer;
        return newAssigned;
      });
      
      // Recargar jugadores existentes
      await loadExistingPlayers();
      
      // Limpiar estados
      setShowSubstitutionModal(false);
      setSubstitutionMode(null);
      setPlayerToRemove(null);
      setSlotToRemove(null);
      setModalError(null);
    } catch (error) {
      setModalError(translateError('Network error'));
    }
  };

  // Eliminar jugador específico del equipo
  const handleRemovePlayerById = async (player: any) => {
    if (!teamId) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const removePlayerRes = await fetch(`${API_BASE_URL}/api/fantasy-teams/${teamId}/players/${player.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!removePlayerRes.ok) {
        const errorData = await removePlayerRes.json();
        setModalError(translateError(errorData.error || errorData.message || 'Error removing player from team'));
        return;
      }

      // Recargar presupuesto
      await fetchBudget();
      
      // Recargar jugadores existentes
      await loadExistingPlayers();
      
      setModalError(null);
    } catch (error) {
      setModalError(translateError('Network error'));
    }
  };

  // Eliminar jugador de slot
  const handleRemovePlayer = async (slotType: 'starter' | 'bench', slotIdx: number) => {
    const key = `${slotType}-${slotIdx}`;
    const playerToRemove = assigned[key];
    
    if (!playerToRemove || !teamId) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Eliminar el jugador del equipo
      const removePlayerRes = await fetch(`${API_BASE_URL}/api/fantasy-teams/${teamId}/players/${playerToRemove.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!removePlayerRes.ok) {
        const errorData = await removePlayerRes.json();
        setModalError(translateError(errorData.error || errorData.message || 'Error removing player from team'));
        return;
      }
      
      // Actualizar estado local inmediatamente
      setAssigned((prev: any) => {
        const newAssigned = { ...prev };
        delete newAssigned[key];
        return newAssigned;
      });
      
      // Actualizar presupuesto localmente (sumar el precio del jugador eliminado)
      setBudget(prev => (prev || 0) + parseFloat(playerToRemove.price || 0));
      
      // Limpiar estados
      setSelectedSlot(null);
      setModalError(null);
      
      // Recargar datos en segundo plano sin bloquear la UI
      setTimeout(async () => {
        await fetchBudget();
        await loadExistingPlayers();
        await fetchPlayers(false); // No mostrar loading para evitar parpadeo
      }, 100);
    } catch (error) {
      setModalError(translateError('Network error'));
    }
  };

  // Filtrar jugadores
  const filteredPlayers = useMemo(() => {
    return players.filter((player: any) => {
      // Filtro por búsqueda
      const matchesSearch = player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           player.team.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtro por posición - Si hay un slot seleccionado, filtrar por su posición
      let matchesPosition = true;
      if (selectedSlot) {
        // Si hay un slot seleccionado, filtrar automáticamente por su posición
        matchesPosition = player.positionType === selectedSlot.positionType;
      } else if (selectedPosition) {
        // Si no hay slot seleccionado pero hay filtro manual de posición
        matchesPosition = player.positionType === selectedPosition;
      }
      
      // Filtro por categoría
      const matchesCategory = selectedCategory.length === 0 || selectedCategory.includes(player.category);
      
      // Filtro por club
      const matchesClub = selectedClubs.length === 0 || selectedClubs.some(club => player.team.toLowerCase().includes(club.toLowerCase()));
      
      // Filtro por precio
      const playerPrice = parseFloat(player.price || 0);
      const matchesPrice = playerPrice >= priceRange[0] && playerPrice <= priceRange[1];
      
      return matchesSearch && matchesPosition && matchesCategory && matchesClub && matchesPrice;
    }).sort((a, b) => {
      if (priceSort === 'price_asc' || priceSort === 'price_desc') {
        const priceA = parseFloat(a.price || 0);
        const priceB = parseFloat(b.price || 0);
        return priceSort === 'price_asc' ? priceA - priceB : priceB - priceA;
      }
      if (priceSort === 'fantasy_asc' || priceSort === 'fantasy_desc') {
        const pointsA = parseFloat(a.totalFantasyPoints || 0);
        const pointsB = parseFloat(b.totalFantasyPoints || 0);
        return priceSort === 'fantasy_asc' ? pointsA - pointsB : pointsB - pointsA;
      }
      return 0;
    });
  }, [players, searchTerm, selectedPosition, selectedCategory, selectedClubs, selectedSlot, priceRange, priceSort]);




  // Efectos
  useEffect(() => {
    fetchPlayers(true); // Mostrar loading en carga inicial
    fetchBudget();
    loadExistingPlayers();
  }, [fetchPlayers, fetchBudget, loadExistingPlayers]);

  // Auto-cerrar modal de error después de 3 segundos
  useEffect(() => {
    if (modalError) {
      const timer = setTimeout(() => {
        setModalError(null);
      }, 3000); // 3 segundos

      return () => clearTimeout(timer);
    }
  }, [modalError]);



  // Mapear jugadores asignados a slots
  const assignedFromLineup = useMemo(() => {
    return assigned;
  }, [assigned]);

  // Calcular composición actual del equipo
  const teamComposition = useMemo(() => {
    const titulares = Object.entries(assigned)
      .filter(([key]) => key.startsWith('starter-'))
      .map(([, player]) => player)
      .filter(Boolean);
    
    const suplentes = Object.entries(assigned)
      .filter(([key]) => key.startsWith('bench-'))
      .map(([, player]) => player)
      .filter(Boolean);
    
    const allPlayers = [...titulares, ...suplentes];
    
    return {
      titulares: {
        perimetral: titulares.filter(p => p.positionType === 'Perimetral').length,
        interior: titulares.filter(p => p.positionType === 'Interior').length,
        total: titulares.length
      },
      suplentes: {
        perimetral: suplentes.filter(p => p.positionType === 'Perimetral').length,
        interior: suplentes.filter(p => p.positionType === 'Interior').length,
        total: suplentes.length
      },
      categorias: {
        extranjero: allPlayers.filter(p => p.category === 'Extranjero').length,
        fichaNacional: allPlayers.filter(p => p.category === 'FichaNacional').length,
        sub23: allPlayers.filter(p => p.category === 'Sub23').length
      }
    };
  }, [assigned]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#001A3E' }}>
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 shadow-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <img src={loadingLogo} alt="FantasyLub Logo" className="w-full h-full object-contain" />
          </div>
          <div className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.5)' }}>Cargando tu equipo...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#001A3E' }}>
        <div className="rounded-xl px-6 py-4 text-sm font-medium" style={{ background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.25)', color: '#fca5a5' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden" style={{ background: '#001A3E' }}>

      {/* Header */}
      <header className="flex-shrink-0 px-4 pt-3 pb-3" style={{ background: 'rgba(0,15,40,0.97)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        {/* Row 1: logo + name + actions */}
        <div className="flex items-center gap-2 mb-2 w-full max-w-[440px] mx-auto lg:max-w-[1100px] lg:px-6">
          <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <img src={fantasyLogo} alt="" className="w-full h-full object-contain" />
          </div>
          <span className="text-base font-black text-white">Fantasy<span style={{ color: '#FFB800' }}>Lub</span></span>
          {teamName && <span className="text-sm truncate max-w-[110px]" style={{ color: 'rgba(255,255,255,0.4)' }}>· {teamName}</span>}
          <div className="flex-1" />
          <button
            onClick={() => setShowInstructions(true)}
            className="p-1.5 rounded-lg transition-all hover:bg-white/10"
            style={{ color: 'rgba(255,255,255,0.4)' }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
          <button
            onClick={handleLogoutClick}
            className="p-1.5 rounded-lg transition-all hover:bg-white/10"
            style={{ color: 'rgba(255,255,255,0.4)' }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
        {/* Row 2: stats */}
        <div className="flex items-center gap-3 w-full max-w-[440px] mx-auto lg:max-w-[1100px] lg:px-6">
          <div className="flex flex-col">
            <span className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.35)' }}>Presupuesto</span>
            <span className="text-base font-black" style={{ color: '#FFB800' }}>${(budget || 0).toFixed(1)}M</span>
          </div>
          <div className="w-px h-8 mx-1" style={{ background: 'rgba(255,255,255,0.1)' }} />
          <div className="flex gap-3 flex-1">
            {[
              { label: 'NAC', val: teamComposition.categorias.fichaNacional, max: 5 },
              { label: 'EXT', val: teamComposition.categorias.extranjero, max: 3 },
              { label: 'S23', val: teamComposition.categorias.sub23, max: 2 },
            ].map(({ label, val, max }) => (
              <div key={label} className="flex flex-col items-center">
                <span className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.35)' }}>{label}</span>
                <span className={`text-base font-black ${val === max ? 'text-green-400' : 'text-white'}`}>{val}<span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.35)' }}>/{max}</span></span>
              </div>
            ))}
          </div>
        </div>
      </header>

      {/* Main content — mobile: una columna. Desktop: cancha a la izquierda,
          banco y acciones a la derecha. */}
      <div className="flex-1 flex flex-col min-h-0 pb-3 gap-2 w-full max-w-[440px] mx-auto lg:max-w-[1100px] lg:flex-row lg:gap-8 lg:px-6 lg:pt-5">

        {/* Court — cuadrada. aspectRatio (y no paddingBottom:100%) para que el alto
            se calcule sobre el ancho propio y no sobre el del contenedor padre. */}
        <div className="relative flex-shrink-0 rounded-b-2xl overflow-hidden lg:rounded-2xl lg:self-start lg:flex-1 lg:max-w-[580px]" style={{ aspectRatio: '1 / 1' }}>
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url('/court-bg.png')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center top',
              backgroundRepeat: 'no-repeat',
              backgroundColor: '#C8935A',
            }}
          >
            {starters.map((slot, idx) => {
              const key = `starter-${idx}`;
              const assignedPlayer = assignedFromLineup[key];
              return (
                <div
                  key={idx}
                  className="absolute flex flex-col items-center"
                  style={{ left: `${slot.x}%`, top: `${slot.y}%`, transform: 'translate(-50%, -50%)' }}
                >
                  <JerseySlot
                    player={assignedPlayer}
                    isStarter={true}
                    slotPositionType={slot.positionType}
                    onClick={() => {
                      if (assignedPlayer) {
                        setPlayerToRemove(assignedPlayer);
                        setSlotToRemove({ type: 'starter', idx });
                        setShowSubstitutionModal(true);
                        setSubstitutionMode(null);
                      } else {
                        setSelectedSlot({ type: 'starter', idx, positionType: slot.positionType });
                      }
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Columna derecha en desktop: banco + acciones */}
        <div className="flex flex-col min-h-0 gap-2 lg:flex-1 lg:gap-4 lg:pb-3">

        {/* Bench — full width with its own internal padding */}
        <div className="flex-shrink-0 mx-3 rounded-2xl pt-3 pb-4 lg:mx-0 lg:pt-5 lg:pb-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <p className="text-[10px] font-bold uppercase tracking-widest text-center mb-3" style={{ color: 'rgba(255,255,255,0.35)' }}>Suplentes</p>
          <div className="flex justify-around">
            {bench.map((slot, idx) => {
              const key = `bench-${idx}`;
              const assignedPlayer = assignedFromLineup[key];
              return (
                <JerseySlot
                  key={idx}
                  player={assignedPlayer}
                  isStarter={false}
                  slotPositionType={slot.positionType}
                  size="bench"
                  onClick={() => {
                    if (assignedPlayer) {
                      setPlayerToRemove(assignedPlayer);
                      setSlotToRemove({ type: 'bench', idx });
                      setShowSubstitutionModal(true);
                      setSubstitutionMode(null);
                    } else {
                      setSelectedSlot({ type: 'bench', idx, positionType: slot.positionType });
                    }
                  }}
                />
              );
            })}
          </div>
        </div>

        {/* Spacer — fills remaining space and pushes buttons to bottom */}
        <div className="flex-1" />

        {/* Action buttons */}
        <div className="flex gap-2 px-3 lg:px-0">
          <button
            onClick={generateRandomTeam}
            disabled={assignedPlayersCount > 0}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)', color: '#c4b5fd' }}
          >
            <span>🎲</span>
            Llenar Automático
          </button>
          <button
            onClick={async () => {
              if (assignedPlayersCount === 10) {
                await clearTeam();
                setBudget(100);
              }
            }}
            disabled={assignedPlayersCount !== 10}
            className="px-3 py-2.5 rounded-xl text-xs font-bold transition-all disabled:opacity-30 disabled:cursor-not-allowed flex items-center"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        {/* Confirm team button */}
        {assignedPlayersCount === 10 && (
          <button
            onClick={() => handleConfirmTeam()}
            className="mx-3 py-3.5 rounded-2xl text-base font-black uppercase tracking-widest transition-all active:scale-95 shadow-lg lg:mx-0"
            style={{ background: '#FFB800', color: '#001A3E' }}
          >
            Confirmar Equipo
          </button>
        )}
        </div>
      </div>

      {/* Bottom sheet — Player selector */}
      {selectedSlot && (
        <div className="fixed inset-0 z-40 flex flex-col justify-end">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSelectedSlot(null)} />
          <div
            className="relative flex flex-col rounded-t-3xl"
            style={{ background: '#0A1F4E', border: '1px solid rgba(255,255,255,0.1)', maxHeight: '78vh' }}
          >
            <div className="pt-3 pb-1 flex justify-center">
              <div className="w-10 h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.2)' }} />
            </div>
            <div className="px-4 pb-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-black text-white text-base">Agregar {selectedSlot.positionType}</h3>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    {selectedSlot.type === 'starter' ? 'Titular' : 'Suplente'} · {filteredPlayers.length} disponibles
                  </p>
                </div>
                <button
                  onClick={() => setSelectedSlot(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-full"
                  style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)' }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'rgba(255,255,255,0.3)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Buscar jugador..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-lub-accent"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}
                  autoFocus
                />
              </div>
              <div className="flex gap-2 mt-2.5 overflow-x-auto pb-0.5">
                {(['Extranjero', 'FichaNacional', 'Sub23'] as const).map((cat) => {
                  const active = selectedCategory.includes(cat);
                  const label = cat === 'Extranjero' ? 'EXT' : cat === 'FichaNacional' ? 'NACIONAL' : 'SUB 23';
                  return (
                    <button
                      key={cat}
                      onClick={() => {
                        if (active) {
                          setSelectedCategory(selectedCategory.filter(c => c !== cat));
                        } else {
                          setSelectedCategory([...selectedCategory, cat]);
                        }
                      }}
                      className="flex-shrink-0 px-3 py-1 rounded-full text-xs font-bold transition-all"
                      style={{
                        background: active ? '#FFB800' : 'rgba(255,255,255,0.07)',
                        color: active ? '#001A3E' : 'rgba(255,255,255,0.5)',
                        border: active ? 'none' : '1px solid rgba(255,255,255,0.1)',
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
                {selectedCategory.length > 0 && (
                  <button
                    onClick={() => setSelectedCategory([])}
                    className="flex-shrink-0 px-3 py-1 rounded-full text-xs font-bold"
                    style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.07)' }}
                  >
                    Limpiar
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-y-auto flex-1 p-3 space-y-2">
              {filteredPlayers.length === 0 ? (
                <div className="text-center py-8 text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>
                  No se encontraron jugadores
                </div>
              ) : (
                filteredPlayers.map((player: any) => {
                  const swapCalc = calculateSwapCost(player, null);
                  const canAfford = swapCalc.netCost <= budget;
                  const isInTeam = assignedPlayerIds.includes(player.id);
                  const currentPlayers = Object.values(assigned).filter(Boolean);
                  const teamCounts: { [key: string]: number } = {};
                  currentPlayers.forEach((p: any) => { if (p?.team) teamCounts[p.team] = (teamCounts[p.team] || 0) + 1; });
                  const hasMaxPlayersFromTeam = player.team && teamCounts[player.team] >= 3;
                  const categoryCounts: { [key: string]: number } = {};
                  currentPlayers.forEach((p: any) => { if (p?.category) categoryCounts[p.category] = (categoryCounts[p.category] || 0) + 1; });
                  const categoryLimits: { [key: string]: number } = { 'Extranjero': 3, 'FichaNacional': 5, 'Sub23': 2 };
                  const hasMaxPlayersFromCategory = player.category && categoryCounts[player.category] >= (categoryLimits[player.category] || 0);
                  const canBePurchased = canAfford && !hasMaxPlayersFromTeam && !hasMaxPlayersFromCategory;
                  const isBeingAdded = playersBeingAdded.has(player.id);
                  const categoryLabel = player.category === 'Extranjero' ? 'EXT' : player.category === 'FichaNacional' ? 'NAC' : 'S23';
                  const blocked = !isInTeam && !canBePurchased;

                  return (
                    <button
                      key={player.id}
                      disabled={isBeingAdded || blocked}
                      onClick={async () => {
                        if (isBeingAdded) return;
                        if (isInTeam) {
                          await handleRemovePlayerById(player);
                        } else if (canBePurchased) {
                          await handleAssign(player);
                        } else if (hasMaxPlayersFromTeam) {
                          setModalError(`Ya tenés 3 jugadores de ${formatTeamName(player.team)}.`);
                        } else if (hasMaxPlayersFromCategory) {
                          setModalError(`Límite de ${player.category} alcanzado.`);
                        } else {
                          setModalError(`Sin presupuesto. Necesitás $${player.price}M, tenés $${budget}M`);
                        }
                      }}
                      className="w-full rounded-xl px-3 py-2.5 text-left transition-all active:scale-95"
                      style={{
                        background: isInTeam ? 'rgba(74,222,128,0.10)' : canBePurchased ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.02)',
                        border: isInTeam ? '1px solid rgba(74,222,128,0.25)' : '1px solid rgba(255,255,255,0.07)',
                        opacity: blocked ? 0.45 : 1,
                        cursor: blocked ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <div className="flex items-center gap-3">
                        {/* Player photo */}
                        <div className="flex-shrink-0 w-10 h-10 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}>
                          {player.sofascoreId ? (
                            <img
                              src={`https://api.sofascore.com/api/v1/player/${player.sofascoreId}/image`}
                              alt={player.name}
                              className="w-full h-full object-cover"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-xs font-black" style={{ color: 'rgba(255,255,255,0.3)' }}>
                              {player.name.charAt(0)}
                            </div>
                          )}
                        </div>
                        {/* Name + club */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-bold text-white text-sm truncate">{player.name}</span>
                            {isInTeam && <span className="text-[9px] font-black text-green-400 flex-shrink-0 bg-green-400/10 px-1 rounded">✓</span>}
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.45)' }}>{formatTeamName(player.team)}</span>
                            <span className="text-[9px] font-bold px-1 py-0.5 rounded" style={{
                              background: player.category === 'Extranjero' ? 'rgba(251,146,60,0.2)' : player.category === 'Sub23' ? 'rgba(96,165,250,0.2)' : 'rgba(255,255,255,0.1)',
                              color: player.category === 'Extranjero' ? '#fb923c' : player.category === 'Sub23' ? '#60a5fa' : 'rgba(255,255,255,0.5)',
                            }}>
                              {categoryLabel}
                            </span>
                          </div>
                        </div>
                        {/* Price + stats */}
                        <div className="text-right flex-shrink-0">
                          <div className="font-black text-sm" style={{ color: '#FFB800' }}>${player.price}M</div>
                          <div className="text-[11px] mt-0.5" style={{ color: 'rgba(74,222,128,0.8)' }}>
                            {Number(player.totalFantasyPoints || 0).toFixed(0)} pts
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error toast */}
      {modalError && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full mx-4">
          <div className="px-4 py-3 rounded-xl shadow-2xl" style={{ background: 'rgba(239,68,68,0.9)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.15)' }}>
            <div className="flex items-center gap-3">
              <svg className="w-4 h-4 flex-shrink-0 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1 text-sm font-medium text-white">{modalError}</div>
              <button onClick={() => setModalError(null)} className="text-white/70 hover:text-white transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Substitution modal */}
      {showSubstitutionModal && playerToRemove && slotToRemove && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-md rounded-2xl p-6 max-h-[90vh] overflow-y-auto" style={{ background: '#0A2055', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-white">{playerToRemove.name}</h3>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  {playerToRemove.positionType === 'Perimetral' ? 'PERIMETRAL' : 'INTERIOR'} · {formatTeamName(playerToRemove.team)}
                </p>
              </div>
              <button
                onClick={() => { setShowSubstitutionModal(false); setSubstitutionMode(null); setPlayerToRemove(null); setSlotToRemove(null); }}
                className="w-8 h-8 flex items-center justify-center rounded-full ml-4 flex-shrink-0"
                style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {!substitutionMode && (
              <div className="space-y-2">
                <p className="text-sm mb-3" style={{ color: 'rgba(255,255,255,0.5)' }}>¿Qué querés hacer?</p>
                <button
                  onClick={() => setSubstitutionMode('substitute')}
                  className="w-full px-4 py-3 rounded-xl text-sm font-bold transition-all"
                  style={{ background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.25)', color: '#4ade80' }}
                >
                  Cambiar jugador
                </button>
                <button
                  onClick={() => setSubstitutionMode('remove')}
                  className="w-full px-4 py-3 rounded-xl text-sm font-bold transition-all"
                  style={{ background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.25)', color: '#fca5a5' }}
                >
                  Eliminar jugador
                </button>
              </div>
            )}
            {substitutionMode === 'remove' && (
              <div className="space-y-3">
                <div className="rounded-xl px-4 py-3 text-sm" style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)', color: '#fca5a5' }}>
                  ¿Seguro que querés eliminar a <strong>{playerToRemove.name}</strong>?
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      if (slotToRemove) await handleRemovePlayer(slotToRemove.type, slotToRemove.idx);
                      setShowSubstitutionModal(false); setSubstitutionMode(null); setPlayerToRemove(null); setSlotToRemove(null);
                    }}
                    className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold"
                    style={{ background: 'rgba(248,113,113,0.15)', border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5' }}
                  >
                    Confirmar
                  </button>
                  <button
                    onClick={() => setSubstitutionMode(null)}
                    className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
            {substitutionMode === 'substitute' && (
              <div>
                <p className="text-sm mb-3" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  Seleccioná un {slotToRemove?.type === 'starter' ? 'suplente' : 'titular'} de la misma posición para intercambiar:
                </p>
                {(() => {
                  const substitutes = getSubstitutesOfSamePosition(playerToRemove.positionType, slotToRemove!.type, slotToRemove!.idx);
                  return (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {substitutes.length > 0 ? (
                        substitutes.map((substitute: any) => (
                          <button
                            key={`${substitute.slotType}-${substitute.slotIdx}`}
                            onClick={() => handleSubstitution(substitute, substitute.slotType, substitute.slotIdx)}
                            className="w-full p-3 rounded-xl text-left transition-all"
                            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                          >
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="font-bold text-white text-sm">{substitute.name}</span>
                              <span className="text-sm font-bold" style={{ color: '#FFB800' }}>${substitute.price}M</span>
                            </div>
                            <div className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
                              {substitute.positionType} · {formatTeamName(substitute.team)}
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="text-center py-4 text-sm" style={{ color: 'rgba(255,255,255,0.35)' }}>
                          No hay {slotToRemove?.type === 'starter' ? 'suplentes' : 'titulares'} de la misma posición para intercambiar.
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Instructions modal */}
      {showInstructions && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-lg rounded-2xl p-6 max-h-[90vh] overflow-y-auto" style={{ background: '#0A2055', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-white">Reglas del equipo</h2>
              <button
                onClick={() => setShowInstructions(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full"
                style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4 text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
              <p>Seleccioná <strong className="text-white">10 jugadores</strong> que cumplan con estas reglas:</p>
              <div className="rounded-xl p-4 space-y-2" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <p className="font-bold text-white">Posiciones</p>
                <p>• <strong>Perimetrales (PER):</strong> Bases, escoltas y aleros</p>
                <p>• <strong>Interiores (INT):</strong> Ala-pívots y pívots</p>
              </div>
              <div className="rounded-xl p-4 space-y-2" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <p className="font-bold text-white">Composición</p>
                <p>• <strong>5 Titulares:</strong> 3 PER + 2 INT</p>
                <p>• <strong>5 Suplentes:</strong> 3 PER + 2 INT</p>
              </div>
              <div className="rounded-xl p-4 space-y-2" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <p className="font-bold text-white">Categorías</p>
                <p>• <strong>3 Extranjeros</strong></p>
                <p>• <strong>5 Fichas Nacionales</strong></p>
                <p>• <strong>2 Sub-23</strong></p>
              </div>
            </div>
            <button
              onClick={() => setShowInstructions(false)}
              className="w-full mt-5 py-3 rounded-xl font-black text-sm uppercase tracking-widest"
              style={{ background: '#FFB800', color: '#001A3E' }}
            >
              ¡Entendido!
            </button>
          </div>
        </div>
      )}

      {/* Logout modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#0A2055', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h2 className="text-xl font-bold text-white mb-2">Cerrar Sesión</h2>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.5)' }}>¿Seguro que querés cerrar sesión?</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}
              >
                Cancelar
              </button>
              <button
                onClick={() => { setShowLogoutModal(false); handleLogout(); }}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold"
                style={{ background: 'rgba(248,113,113,0.15)', border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5' }}
              >
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamCourtPage;
